import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, FilterMetadata, FilterService, MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { Defect, DefectMetadata } from '../../shared/models/defect.model';
import { DefectService } from '../../shared/services/defect.service';

/**
 * The list's default view: every defect that has not been accepted yet.
 * Returns a fresh array each call -- PrimeNG mutates filter metadata in place.
 */
const openStatusFilter = (): FilterMetadata[] => [
  { value: ['accepted'], matchMode: 'notIn' },
];

@Component({
  selector: 'app-defects-page',
  imports: [
    CommonModule,
    CardModule,
    DatePickerModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    TableModule,
    ButtonModule,
    IconFieldModule,
    InputTextModule,
    ConfirmDialogModule,
    MultiSelectModule,
    TagModule,
  ],
  providers: [
    MessageService,
    DefectService,
    ConfirmationService,
    TranslateService,
  ],
  templateUrl: './defects-page.component.html',
  styleUrl: './defects-page.component.scss',
})
export class DefectsPageComponent implements OnInit {
  @ViewChild('defectsTable') defectsTable!: Table;
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #filterService = inject(FilterService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  defectService = inject(DefectService);

  public globalFilterFields = DefectMetadata.globalFilterFields;
  public defectColumns = DefectMetadata.defectColumns;

  public defaultFilters: { [key: string]: FilterMetadata[] } = {
    status: openStatusFilter(),
  };

  statusMatchModeOptions: SelectItem[] = [];

  filterOptions = computed(() => {
    const defects = this.defectService.defects();
    const t = (key: string) => this.#translateService.instant(key);
    const options = (prefix: string, values: string[]) =>
      values.map((v) => ({ label: t(`${prefix}.${v}`), value: v }));
    return {
      model_type: options('defect.types', ['route_section', 'psa']),
      model_name: [...new Set(defects.map((d) => d.model_name).filter(Boolean))]
        .sort()
        .map((name) => ({ label: name, value: name })),
      status: options('defect.statuses', ['new', 'in_progress', 'work_done', 'accepted']),
      priority: options('defect.priorities', ['low', 'medium', 'high', 'critical']),
      responsible_user_name: [
        { label: t('defect.notAssigned'), value: null },
        ...[...new Set(defects.map((d) => d.responsible_user_name).filter(Boolean))]
          .sort()
          .map((name) => ({ label: name as string, value: name as string })),
      ],
    } as Record<string, { label: string; value: any }[]>;
  });

  ngOnInit(): void {
    this.#filterService.register('notIn', (value: any, filter: any[]) => {
      if (!filter || !filter.length) return true;
      return !filter.includes(value);
    });

    this.statusMatchModeOptions = [
      { label: this.#translateService.instant('filter.in'), value: 'in' },
      { label: this.#translateService.instant('filter.notIn'), value: 'notIn' },
    ];

    const modelName = this.#activatedRoute.snapshot.queryParamMap.get('modelName');
    const onlyOpen =
      this.#activatedRoute.snapshot.queryParamMap.get('status') === 'open';

    // Each query param owns its own clause. A model-scoped link without
    // status=open keeps showing every status, which is what the defect-history
    // entry point relies on.
    const filters: { [key: string]: FilterMetadata[] } = {};
    if (modelName) {
      filters['model_name'] = [{ value: [modelName], matchMode: 'in' }];
    }
    if (onlyOpen || !modelName) {
      filters['status'] = openStatusFilter();
    }
    this.defaultFilters = filters;

    this.defectService.getDefects();
  }

  navigateToCreate() {
    this.#router.navigate(['/defects/new']);
  }

  navigateToDetail(defect: Defect) {
    this.#router.navigate(['/defects', defect.id]);
  }

  onRowDeleteInit(event: Event, defect: Defect) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant('defect.deleteConfirmMessage'),
      header: this.#translateService.instant('defect.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.defectService.deleteDefect(defect.id).subscribe({
          next: () => {
            this.defectService.getDefects();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
      reject: () => {
        //nothing
      },
    });
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'new':
        return 'info';
      case 'in_progress':
        return 'warn';
      case 'work_done':
        return 'secondary';
      case 'accepted':
        return 'success';
      default:
        return 'secondary';
    }
  }

  getStatusLabel(status: string): string {
    return this.#translateService.instant(`defect.statuses.${status}`);
  }

  getPriorityLabel(priority: string): string {
    return this.#translateService.instant(`defect.priorities.${priority}`);
  }

  getPrioritySeverity(
    priority: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (priority) {
      case 'low':
        return 'info';
      case 'medium':
        return 'warn';
      case 'high':
        return 'danger';
      case 'critical':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getTypeLabel(modelType: string): string {
    return this.#translateService.instant(`defect.types.${modelType}`);
  }
}
